// Bonza Business Value Estimator - Calculation Engine

import {
  HIGH_DEMAND_INDUSTRIES,
  STEADY_DEMAND_INDUSTRIES,
  REVENUE_BASED_SUBCATEGORIES
} from './constants';

/**
 * Get base profit multiples based on owner earnings
 */
export function getBaseMultiples(ownerEarnings) {
  if (ownerEarnings <= 0) {
    return { low: 0, high: 0 };
  }
  if (ownerEarnings <= 100000) {
    return { low: 1.0, high: 1.5 };
  }
  if (ownerEarnings <= 200000) {
    return { low: 1.5, high: 2.0 };
  }
  if (ownerEarnings <= 300000) {
    return { low: 2.0, high: 2.5 };
  }
  if (ownerEarnings <= 500000) {
    return { low: 2.5, high: 3.0 };
  }
  return { low: 3.0, high: 4.0 };
}

/**
 * Calculate lease adjustment
 */
export function getLeaseAdjustment(hasLease, leaseLength) {
  if (!hasLease) return 0;
  
  switch (leaseLength) {
    case '3-4':
    case '4-6':
    case 'over-6':
      return 0.05;
    default:
      return 0;
  }
}

/**
 * Calculate revenue trend adjustment
 */
export function getRevenueTrendAdjustment(revenueTrend) {
  switch (revenueTrend) {
    case 'significantly-increasing':
      return 0.05;
    case 'slightly-increasing':
      return 0.03;
    case 'stable':
      return 0;
    case 'slightly-decreasing':
      return -0.05;
    case 'significantly-decreasing':
      return -0.10;
    default:
      return 0;
  }
}

/**
 * Get buyer demand category based on industry and subcategory
 */
export function getBuyerDemand(industry, subcategory) {
  // Check subcategory first for special cases
  if (subcategory && REVENUE_BASED_SUBCATEGORIES.includes(subcategory)) {
    return 'High';
  }
  
  if (HIGH_DEMAND_INDUSTRIES.includes(industry)) {
    return 'High';
  }
  
  if (STEADY_DEMAND_INDUSTRIES.includes(industry)) {
    return 'Steady';
  }
  
  return 'Specialised';
}

/**
 * Get buyer demand description text
 */
export function getBuyerDemandText(demandCategory) {
  switch (demandCategory) {
    case 'High':
      return 'There is strong buyer demand for businesses like yours. Well-run operations typically attract significant enquiry and competitive interest.';
    case 'Steady':
      return 'There is consistent buyer demand in your sector. Buyers tend to be selective, but serious purchasers are actively searching for established businesses.';
    case 'Specialised':
      return 'This type of business attracts fewer enquiries, but buyers are usually highly motivated and knowledgeable. Specialised businesses often sell very well due to higher barriers to entry.';
    default:
      return '';
  }
}

/**
 * Generate business strengths based on form data
 */
export function generateStrengths(data) {
  const strengths = [];
  
  // Revenue trend strength
  if (data.revenueTrend === 'significantly-increasing') {
    strengths.push('Strong revenue growth trajectory indicates healthy market demand');
  } else if (data.revenueTrend === 'slightly-increasing') {
    strengths.push('Positive revenue growth shows business momentum');
  } else if (data.revenueTrend === 'stable') {
    strengths.push('Stable revenue demonstrates consistent performance and reliable income');
  }
  
  // Lease strength
  if (data.hasLease && ['4-6', 'over-6'].includes(data.leaseLength)) {
    strengths.push('Long-term lease security provides stability for incoming buyers');
  } else if (data.hasLease && data.leaseLength === '3-4') {
    strengths.push('Reasonable lease term remaining offers buyer confidence');
  }
  
  // P&E value
  if (data.plantEquipmentValue > 50000) {
    strengths.push('Significant plant and equipment assets add tangible value');
  }
  
  // Earnings strength
  if (data.ownerEarnings > 300000) {
    strengths.push('Strong owner earnings attract serious, qualified buyers');
  } else if (data.ownerEarnings > 150000) {
    strengths.push('Solid earnings provide attractive return for owner-operators');
  }
  
  // Ensure we have at least 3 strengths
  if (strengths.length < 3) {
    strengths.push('Established business with defined systems and processes');
  }
  if (strengths.length < 3) {
    strengths.push('Opportunity for new owner to implement growth strategies');
  }
  
  return strengths.slice(0, 5);
}

/**
 * Main calculation function
 */
export function calculateValuation(data) {
  const {
    industry,
    subcategory,
    revenueTrend,
    annualRevenue,
    ownerEarnings,
    plantEquipmentValue,
    hasLease,
    leaseLength
  } = data;
  
  let calculationMethod = 'Profit Multiple Method';
  let alternativeValues = null;
  
  // Get base multiples
  const baseMultiples = getBaseMultiples(ownerEarnings);
  
  // Calculate adjustments
  const adjustments = {
    lease: getLeaseAdjustment(hasLease, leaseLength),
    revenueTrend: getRevenueTrendAdjustment(revenueTrend)
  };
  
  // Calculate total adjustment (capped between -10% and +20%)
  let totalAdjustment = Object.values(adjustments).reduce((a, b) => a + b, 0);
  totalAdjustment = Math.max(-0.10, Math.min(0.20, totalAdjustment));
  
  // Calculate base range
  let valuationLow = ownerEarnings * baseMultiples.low;
  let valuationHigh = ownerEarnings * baseMultiples.high;
  
  // Apply adjustments
  valuationLow = valuationLow * (1 + totalAdjustment);
  valuationHigh = valuationHigh * (1 + totalAdjustment);
  
  // Finance (0.8–1.4x revenue) and Cleaning / Pest Control / Gardening (0.8–1.2x revenue):
  // show whichever of the profit or revenue method is higher, the other as the alternative
  const isFinance = industry === 'Finance';
  if (isFinance || (subcategory && REVENUE_BASED_SUBCATEGORIES.includes(subcategory))) {
    if (annualRevenue > 0) {
      const revenueLow = annualRevenue * 0.8;
      const revenueHigh = annualRevenue * (isFinance ? 1.4 : 1.2);
      if (revenueHigh > valuationHigh) {
        alternativeValues = { profitBased: { low: valuationLow, high: valuationHigh } };
        valuationLow = revenueLow;
        valuationHigh = revenueHigh;
        calculationMethod = 'Revenue Multiple Method (with Profit Alternative)';
      } else {
        alternativeValues = { revenueBased: { low: revenueLow, high: revenueHigh } };
        calculationMethod = 'Profit Multiple Method (with Revenue Alternative)';
      }
    }
  }
  // P&E-based pricing: When P&E is higher than earnings
  else if (plantEquipmentValue > ownerEarnings) {
    if (ownerEarnings > 0) {
      // P&E > Earnings but earnings are positive: 100% P&E + 1 year earnings
      const peValue = plantEquipmentValue + ownerEarnings;
      valuationLow = peValue * 0.9;
      valuationHigh = peValue * 1.1;
      calculationMethod = 'Turnkey Opportunity (P&E + Earnings)';
    } else {
      // P&E > Earnings and earnings are negative/zero: 80% P&E only
      valuationLow = plantEquipmentValue * 0.7;
      valuationHigh = plantEquipmentValue * 0.9;
      calculationMethod = 'Turnkey Opportunity (P&E Based)';
    }
  }
  
  // Ensure non-negative values
  valuationLow = Math.max(0, valuationLow);
  valuationHigh = Math.max(valuationLow, valuationHigh);
  
  // Round to nearest $1,000
  valuationLow = Math.round(valuationLow / 1000) * 1000;
  valuationHigh = Math.round(valuationHigh / 1000) * 1000;
  
  // Get buyer demand
  const buyerDemandCategory = getBuyerDemand(industry, subcategory);
  
  // Generate strengths
  const businessStrengths = generateStrengths(data);
  
  return {
    valuationLow,
    valuationHigh,
    baseMultipleLow: baseMultiples.low,
    baseMultipleHigh: baseMultiples.high,
    adjustmentsApplied: adjustments,
    totalAdjustment,
    calculationMethod,
    alternativeValues,
    buyerDemandCategory,
    businessStrengths
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount) {
  if (amount === 0) return '$0';
  if (amount < 0) return `-$${Math.abs(amount).toLocaleString('en-AU')}`;
  return `$${amount.toLocaleString('en-AU')}`;
}

/**
 * Get method explanation text
 */
export function getMethodExplanation(method, data) {
  switch (method) {
    case 'Profit Multiple Method':
      return `We applied a profit multiple of ${data.baseMultipleLow.toFixed(1)}x to ${data.baseMultipleHigh.toFixed(1)}x based on your owner earnings of ${formatCurrency(data.ownerEarnings)}. This multiple reflects current market conditions for businesses in your earnings bracket.`;
    
    case 'Revenue Multiple Method (Finance)':
      return `For finance businesses, we use a revenue-based valuation. We applied a multiple of 0.8x to 1.4x to your annual revenue of ${formatCurrency(data.annualRevenue)}.`;
    
    case 'Profit Multiple Method (with Revenue Alternative)':
      return `We used a profit multiple method as the primary valuation. For businesses in your industry, buyers also consider revenue-based valuations, which we've shown as an alternative.`;
    
    case 'Revenue Multiple Method (with Profit Alternative)': {
      const multiple = data.industry === 'Finance' ? '1.4x' : '1.2x';
      return `For businesses in your industry, buyers commonly value on revenue. We applied a multiple of 0.8x to ${multiple} to your annual revenue of ${formatCurrency(data.annualRevenue)}, which produced a higher result than the profit multiple method shown as an alternative below.`;
    }
    
    case 'Turnkey Opportunity (P&E + Earnings)':
      return `This valuation is P&E based, pricing your business as a turnkey opportunity for buyers looking to enter the industry. We've calculated the full value of your plant and equipment (${formatCurrency(data.plantEquipmentValue)}) plus one year of positive earnings (${formatCurrency(data.ownerEarnings)}), reflecting the value of a ready-to-operate business with existing profitability.`;
    
    case 'Turnkey Opportunity (P&E Based)':
      return `This valuation is P&E based, pricing your business as a turnkey opportunity for buyers looking to enter the industry. Based on your plant, equipment and fit-out value of ${formatCurrency(data.plantEquipmentValue)}, this represents the opportunity for a buyer to acquire a fully equipped operation and implement their own business strategy.`;
    
    default:
      return 'This valuation is based on current market conditions and the information provided.';
  }
}
